import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { formatDuration } from "@/lib/utils";
import { CreateVideoDialog, YoutubeVideoDialog } from "../LibraryPage";
import { useWorkspace, useWorkspaceCan } from "./WorkspaceContext";

type VideoRow = {
  id: string;
  title: string;
  category: string;
  accessTier: string;
  status: string;
  thumbnailUrl: string | null;
  durationSeconds: number | null;
  categoryTag?: string | null;
  source?: "mux" | "youtube";
};

// CEO Rev 1.0 — S4. Campaign-scoped Media: the Library filtered to THIS
// campaign by context (no dropdown). Cross-campaign media never appears here.
// Post-cutover this tab is ALSO the upload surface (the global Library page is
// retired under strict — upload had no UI path at all; ported from Codex's
// access-rights fix). The upload dialogs lock the entered campaign in.
export function MediaTab() {
  const qc = useQueryClient();
  const { campaignId } = useWorkspace();
  // uploadMedia = super_admin | OWNING campaign_owner | content_producer.
  const { uploadMedia } = useWorkspaceCan();
  const [createOpen, setCreateOpen] = useState(false);
  const [youtubeOpen, setYoutubeOpen] = useState(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ["workspace-media", campaignId],
    queryFn: () => api.get<{ items: VideoRow[] }>(`/api/admin/videos?campaignId=${campaignId}`),
  });
  const items = data?.items ?? [];
  // Only needed so the (locked) campaign select in the dialogs can render the
  // campaign's name.
  const { data: campaignData } = useQuery({
    queryKey: ["campaigns-mini"],
    queryFn: () => api.get<{ items: Array<{ id: string; name: string }> }>("/api/admin/campaigns"),
    enabled: uploadMedia,
  });
  const campaigns = campaignData?.items ?? [];

  function refreshMedia() {
    qc.invalidateQueries({ queryKey: ["workspace-media", campaignId] });
    qc.invalidateQueries({ queryKey: ["library"] });
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-head text-lg font-semibold">Media</h2>
          <p className="text-xs text-muted-foreground">Videos belonging to this campaign.</p>
        </div>
        {uploadMedia && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setYoutubeOpen(true)}>
              Add YouTube video
            </Button>
            <Button size="sm" onClick={() => setCreateOpen(true)}>
              Upload video
            </Button>
          </div>
        )}
      </div>

      {isLoading && <p className="text-sm text-muted-foreground">Loading…</p>}

      {/* Same fix as the Library grid: a failed list must not render as
          "no media" (QA defect 2502 — silent 4xx read as vanished uploads). */}
      {!isLoading && error && (
        <Card>
          <div className="py-8 px-6 text-center space-y-2">
            <div className="font-head text-lg">Couldn't load media</div>
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : "Request failed"}
            </p>
          </div>
        </Card>
      )}

      {!isLoading && !error && items.length === 0 && (
        <Card>
          <div className="py-12 px-6 text-center space-y-3">
            <div className="font-head text-lg">No media in this campaign yet</div>
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              {uploadMedia
                ? "Upload a video for this campaign and it will appear here."
                : "Media assigned to this campaign will appear here."}
            </p>
            {uploadMedia && <Button onClick={() => setCreateOpen(true)}>Upload video</Button>}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {items.map((v) => (
          <Link key={v.id} to={`/admin/library/${v.id}`}>
            <Card className="overflow-hidden h-full hover:border-gold transition-colors">
              <div className="aspect-video bg-navy-700 relative">
                {v.thumbnailUrl ? (
                  <img src={v.thumbnailUrl} alt="" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs text-muted-foreground">
                    No thumbnail
                  </div>
                )}
                <Badge
                  className="absolute top-2 left-2"
                  variant={v.status === "approved" ? "ok" : v.status === "pending_approval" ? "default" : "muted"}
                >
                  {v.status.replace(/_/g, " ")}
                </Badge>
                {v.durationSeconds && (
                  <span className="absolute bottom-2 right-2 text-xs bg-black/70 text-white px-1.5 py-0.5 rounded">
                    {formatDuration(v.durationSeconds)}
                  </span>
                )}
              </div>
              <div className="p-3 space-y-1">
                <div className="font-medium text-sm line-clamp-1">{v.title}</div>
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="outline">{v.category.replace(/_/g, " ")}</Badge>
                  <Badge variant="muted">{v.accessTier.replace(/_/g, " ")}</Badge>
                  {v.categoryTag && <Badge variant="outline">{v.categoryTag}</Badge>}
                  {v.source === "youtube" && <Badge variant="outline">YouTube</Badge>}
                </div>
              </div>
            </Card>
          </Link>
        ))}
      </div>

      <CreateVideoDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        campaigns={campaigns}
        initialCampaignId={campaignId}
        lockCampaign
        onCreated={refreshMedia}
      />
      <YoutubeVideoDialog
        open={youtubeOpen}
        onOpenChange={setYoutubeOpen}
        campaigns={campaigns}
        initialCampaignId={campaignId}
        lockCampaign
        onCreated={refreshMedia}
      />
    </div>
  );
}
